/**
 * AI Edit hotkey (CRE report editor). Per user, customizable; works while the
 * CRE page is focused. Default Ctrl+Shift+E (F2–F4 and F6–F10 are already
 * used by the editor / SpeechMike hotkeys).
 */
import { useEffect, useRef } from "react";

export const DEFAULT_AI_EDIT_HOTKEY = { code: "KeyE", ctrl: true, shift: true, alt: false, meta: false };

/** Keys the page already uses (with any modifier), plus browser keys F1, F5, F11, F12. */
const RESERVED_CODES = new Set(["F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12"]);
const MODIFIER_CODES = new Set([
  "ShiftLeft", "ShiftRight", "ControlLeft", "ControlRight", "AltLeft", "AltRight", "MetaLeft", "MetaRight",
]);
/** Common editing / browser shortcuts that must keep working. */
const RESERVED_COMBOS = new Set(
  ["KeyA", "KeyC", "KeyV", "KeyX", "KeyZ", "KeyY", "KeyS", "KeyP", "KeyF", "KeyR", "KeyT", "KeyW", "KeyN", "KeyL", "KeyB", "KeyI", "KeyU"].map(
    (c) => `ctrl:${c}`,
  ),
);

function isValidShape(hk) {
  return !!hk && typeof hk === "object" && typeof hk.code === "string" && hk.code.length > 0;
}

/** The stored value, or the default when it is missing/invalid. */
export function resolveHotkey(hk) {
  return isValidShape(hk) ? hk : DEFAULT_AI_EDIT_HOTKEY;
}

export function matchesHotkey(e, hk) {
  const h = resolveHotkey(hk);
  return (
    e.code === h.code &&
    !!e.ctrlKey === !!h.ctrl &&
    !!e.shiftKey === !!h.shift &&
    !!e.altKey === !!h.alt &&
    !!e.metaKey === !!h.meta
  );
}

function keyName(code) {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^F\d{1,2}$/.test(code)) return code;
  const names = { Space: "Space", Backquote: "`", Minus: "-", Equal: "=", Slash: "/", Period: ".", Comma: "," };
  return names[code] || code;
}

export function hotkeyLabel(hk, isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "")) {
  const h = resolveHotkey(hk);
  const parts = [];
  if (h.ctrl) parts.push(isMac ? "⌃" : "Ctrl");
  if (h.alt) parts.push(isMac ? "⌥" : "Alt");
  if (h.shift) parts.push(isMac ? "⇧" : "Shift");
  if (h.meta) parts.push(isMac ? "⌘" : "Win");
  parts.push(keyName(h.code));
  return parts.join(isMac ? "" : "+");
}

/**
 * Key event → hotkey, or { error } when it cannot be used.
 * Rules: needs Ctrl, Alt or Cmd (Shift alone would block typing); F-keys and
 * common editing shortcuts are reserved.
 */
export function hotkeyFromEvent(e) {
  if (!e || !e.code || MODIFIER_CODES.has(e.code)) return null;
  const hk = { code: e.code, ctrl: !!e.ctrlKey, shift: !!e.shiftKey, alt: !!e.altKey, meta: !!e.metaKey };
  if (RESERVED_CODES.has(hk.code)) return { error: "reserved" };
  if (!hk.ctrl && !hk.alt && !hk.meta) return { error: "needsModifier" };
  if ((hk.ctrl || hk.meta) && !hk.shift && !hk.alt && RESERVED_COMBOS.has(`ctrl:${hk.code}`)) {
    return { error: "reserved" };
  }
  return { hotkey: hk };
}

/** Listens on the window (capture) and calls onTrigger for the hotkey. */
export function useAiEditHotkey({ enabled, hotkey, onTrigger }) {
  const latest = useRef({ hotkey, onTrigger });
  latest.current = { hotkey, onTrigger };
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (e) => {
      if (e.repeat || e.defaultPrevented) return;
      if (e.target && e.target.closest && e.target.closest("[data-hotkey-capture]")) return;
      if (!matchesHotkey(e, latest.current.hotkey)) return;
      e.preventDefault();
      e.stopPropagation();
      latest.current.onTrigger?.();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled]);
}
